import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";
import _ from "lodash";
import { ButtonText, InputText } from "@loanbossinc/component-library";

import { actionCreators as drawerActionCreators } from "../../lenderReportingDrawer.store";
import { Title } from "../styles";

import { actionCreators as sourceFileFieldActionCreators } from "./sourceFileField.store";
import convertMappingsToGridData from "./helpers";
import DataGrid from "./dataGrid";
import {
  NameWrapper,
  UploadDate,
  LevelWrapper,
  RangeWrapper,
  RangeSelectionHeader,
  NavigationWrapper,
  ButtonsWrapper,
  CellsSelected,
  PaddedWrapper,
  StyledSecondaryButton,
  StyledPrimaryButton,
  SearchAndFileWrapper,
  StyledNameDateWrapper
} from "./sourceFileFieldStyles";

const SourceFileDataSelection = props => {
  const {
    deal,
    gridRawData,
    sourceFileDefaultMappings,
    isAnnualized,
    isRangeChanged,
    rowAnchor1ColumnIndex,
    dataColumnOffset,
    rowIndexSelected,
    dataGridRows,
    dataColumnAnchorIndex,
    amountOfCellsSelected,
    dataSelectDirection,
    onDataSelected,
    selectedField,
    selectedSourceFile,
    isEditing,
    setDrawerSelections,
    setFieldSelections,
    setSourceFileFieldSelections,
    getGridDataByFileId,
    getDefaultMappingsForFile,
    rowsSelected,
    fieldValueToShowAndSave,
    filteredSelectedRows
  } = props;

  const [filteredRows, setFilteredRows] = useState(null);

  useEffect(
    () => () => {
      setDrawerSelections({ drawerMinWidth: "800px" });
      setFieldSelections({ isSelectingField: false });
    },
    []
  );

  useEffect(() => {
    getGridDataByFileId({ sourceFileId: `${selectedSourceFile.id}-0-json` });
    if (selectedSourceFile) {
      const { sourceId, typeId } = selectedSourceFile;
      getDefaultMappingsForFile({ vendorTypeId: sourceId, mappingTypeId: typeId });
    }
  }, [selectedSourceFile]);

  const mappedRawDataToGrid = useMemo(() => {
    if (gridRawData && sourceFileDefaultMappings) {
      return convertMappingsToGridData(gridRawData, sourceFileDefaultMappings);
    }

    return null;
  }, [gridRawData, sourceFileDefaultMappings]);

  useEffect(() => {
    if (mappedRawDataToGrid) {
      const { rowsForGrid, columnsForGrid } = mappedRawDataToGrid;
      setSourceFileFieldSelections({
        dataGridRows: rowsForGrid,
        dataGridColumns: columnsForGrid,
        dataColumnAnchorIndex: sourceFileDefaultMappings.dataAnchorColumnIndex,
        numExpectedPeriods: sourceFileDefaultMappings.numExpectedPeriods,
        dataSelectDirection: sourceFileDefaultMappings.dataSelectDirection,
        rowAnchor1ColumnIndex: sourceFileDefaultMappings.lineItemCoAColumnIndex,
        rowAnchor2ColumnIndex: sourceFileDefaultMappings.lineItemDescriptionColumnIndex
      });
    }
  }, [mappedRawDataToGrid]);

  useEffect(() => {
    // ? Logic needed if dataAnchorColumnIndex from field !== from defaultMappings
    if (isEditing) {
      const rowIdxSelected = dataGridRows.find(r => r.row[selectedField.rowAnchor1ColumnIndex] === selectedField.rowAnchor1Value &&
        r.row[selectedField.rowAnchor2ColumnIndex] === selectedField.rowAnchor2Value)?.rowIndex;
      if((!rowsSelected || rowsSelected.length === 0) && rowIdxSelected) {
          const legacySelectedField = {
            dataColumnOffset: selectedField.dataColumnOffset,
            dataSelectDirection,
            dataColumnAnchorIndex: selectedField.dataColumnAnchorIndex,
            amountOfCellsSelected: Math.abs(selectedField.dataColumnOffset) + 1,
            fieldValue: selectedField.value,
            rowIndexSelected: rowIdxSelected,
            rowAnchor1ColumnIndex: selectedField.rowAnchor1ColumnIndex,
            rowAnchor1Value: selectedField.rowAnchor1Value,
            rowAnchor2ColumnIndex: selectedField.rowAnchor2ColumnIndex,
            rowAnchor2Value: selectedField.rowAnchor2Value
          };
          setSourceFileFieldSelections({rowsSelected: [legacySelectedField]});
      }
      else {
         setSourceFileFieldSelections({ rowsSelected });
      }
    }
  }, [selectedField, dataGridRows, selectedSourceFile]);

  const getSourceFileInfo = () => {
    let summary = "Deal";
    if (selectedSourceFile.propertyId && selectedSourceFile.propertyId !== "-1") {
      const dealProperty = Boolean(deal) && deal.properties.find(prop => prop.id === selectedSourceFile.propertyId);
      summary = dealProperty ? dealProperty.name : "Property";
    }

    return (
      <StyledNameDateWrapper>
        <NameWrapper style={{ textAlign: "end" }}>{_.startCase(`${selectedSourceFile.sourceName} ${selectedSourceFile.typeName}`)}</NameWrapper>
        <UploadDate>{`Uploaded on ${moment.utc(selectedSourceFile.createdAt).format("MMMM DD, YYYY")}`}</UploadDate>
        <LevelWrapper style={{ padding: 0 }}>{summary}</LevelWrapper>
      </StyledNameDateWrapper>
    );
  };

  const handleSearch = e => {
    e.persist();

    const getFilteredRows = searchValue => {
      if (!dataGridRows.length) {
        return dataGridRows;
      }

      return dataGridRows.filter(r => {
        const colIdxToFilterBy = sourceFileDefaultMappings?.lineItemDescriptionColumnIndex || 0;
        if (typeof r.row[colIdxToFilterBy] !== "string") {
          return false;
        }

        return r.row[colIdxToFilterBy].toLowerCase().includes(searchValue.toLowerCase());
      });
    };

    const handleSearchDebounce = _.debounce(event => {
      if (event.target.value) {
        setFilteredRows(getFilteredRows(event.target.value));
      } else {
        setFilteredRows(dataGridRows);
      }
    }, 500);

    handleSearchDebounce(e);
  };

  const onDoneClick = () => {
    // calculate total in selected range
    let fieldValue;
    const numOfMonthRows = rowsSelected.length;
    const monthsInYear = 12;
    const amountOfMonths = monthsInYear * numOfMonthRows;
    if (isAnnualized) {
      fieldValue = fieldValueToShowAndSave / amountOfCellsSelected * amountOfMonths;
    } else {
      fieldValue = fieldValueToShowAndSave;
    }

    setSourceFileFieldSelections({ fieldValue });
    onDataSelected();
  };

  const onCancel = () => {
    setSourceFileFieldSelections({
      dataColumnOffset: null,
      rowIndexSelected: null,
      filteredRowsSelectedIndex: null,
      amountOfCellsSelected: null,
      fieldValue: null,
      isRangeChanged: false,
      rowsSelected: []
    });
    onDataSelected();
  };
  const isDoneButtonDisabled = !isRangeChanged || amountOfCellsSelected < 1 || amountOfCellsSelected === null;
  return (
    <RangeWrapper>
      <RangeSelectionHeader>
        <NavigationWrapper>
          <ButtonText id="back-button" onClick={onCancel} label="" reverse iconName="arrow_forward" />
          <Title fontSize="16px" paddingTop="5px">
            Select a row to highlight a range
          </Title>
        </NavigationWrapper>
        <ButtonsWrapper>
          <CellsSelected>{`${amountOfCellsSelected || 0} ${amountOfCellsSelected === 1 ? "cell" : "cells"} selected`}</CellsSelected>
          <PaddedWrapper>
            <StyledSecondaryButton size="sm" label="Cancel" onClick={onCancel} />
          </PaddedWrapper>
          <PaddedWrapper>
            <StyledPrimaryButton size="sm" label="Done" disabled={isDoneButtonDisabled} onClick={onDoneClick} />
          </PaddedWrapper>
        </ButtonsWrapper>
      </RangeSelectionHeader>
      <SearchAndFileWrapper>
        <InputText variant="filled" label="Search for a line item" name="line-item" fullWidth onChange={handleSearch} />
        {getSourceFileInfo()}
      </SearchAndFileWrapper>
      <DataGrid {...props} filteredRows={filteredRows || dataGridRows} />
    </RangeWrapper>
  );
};

const mapStateToProps = ({ lenderReportSourceFileField }) => ({ ...lenderReportSourceFileField });

const mapDispatchToProps = dispatch => bindActionCreators({ ...drawerActionCreators, ...sourceFileFieldActionCreators }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SourceFileDataSelection);
